/**
 * Mock in-browser "Supabase" for Jain Lexicon (localStorage-backed).
 *
 * This is **not** `@supabase/supabase-js` and does **not** connect to Postgres.
 * Used only where features still query `supabase.from(...)` locally (e.g. Word of the Day,
 * legacy term rows until fully migrated to `termsApi`).
 *
 * **Auth** does not use this file: login/signup/session use `authApi` against
 * `VITE_API_BASE_URL` (`/jain_lexicon/auth/...`). There is no `/auth/me/` call.
 */
type TermStatus = "pending" | "approved" | "rejected";
type UserRole =
  | "admin"
  | "moderator"
  | "user"
  | "scholar"
  | "contributor";

type TermRecord = {
  id: string;
  word: string;
  meaning_english: string | null;
  meaning_hindi: string | null;
  meaning_gujarati: string | null;
  pronunciation: string | null;
  example_usage: string | null;
  status: TermStatus;
  contributor_email: string | null;
  contributor_name: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  featured: boolean;
  audio_url: string | null;
};

type ProfileRecord = {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type RoleRecord = {
  id: string;
  user_id: string;
  role: UserRole;
  created_at: string;
};

type DB = {
  terms: TermRecord[];
  profiles: ProfileRecord[];
  user_roles: RoleRecord[];
};

type AuthUser = { id: string; email: string | null };
type AuthSession = { user: AuthUser };
type AuthChangeEvent = "SIGNED_IN" | "SIGNED_OUT";
type AuthListener = (
  _event: AuthChangeEvent,
  session: AuthSession | null
) => void;

const STORAGE_KEY = "jain-lexicon-mock-db";
const SESSION_KEY = "jain-lexicon-mock-session";

const nowIso = () => new Date().toISOString();
const genId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const seedTerms: TermRecord[] = [];

const seedProfiles: ProfileRecord[] = [
  {
    id: "profile-admin",
    user_id: "user-admin",
    email: "admin@admin.com",
    display_name: "Admin",
    is_active: true,
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "profile-user",
    user_id: "user-user",
    email: "user@user.com",
    display_name: "User",
    is_active: true,
    created_at: nowIso(),
    updated_at: nowIso(),
  },
  {
    id: "profile-contributor",
    user_id: "user-contributor",
    email: "hiren@gmail.com",
    display_name: "Contributor",
    is_active: true,
    created_at: nowIso(),
    updated_at: nowIso(),
  },
];

const seedRoles: RoleRecord[] = [
  {
    id: "role-admin",
    user_id: "user-admin",
    role: "admin",
    created_at: nowIso(),
  },
  {
    id: "role-scholar",
    user_id: "user-user",
    role: "scholar",
    created_at: nowIso(),
  },
  {
    id: "role-contributor",
    user_id: "user-contributor",
    role: "contributor",
    created_at: nowIso(),
  },
];

const loadDb = (): DB => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as DB;
      // Remove legacy seeded demo records so UI only shows fetched/user data.
      const cleanedTerms = (parsed.terms || []).filter(
        term => term.id !== "term-ahimsa" && term.id !== "term-anekantavada"
      );
      if (cleanedTerms.length !== (parsed.terms || []).length) {
        const cleanedDb: DB = { ...parsed, terms: cleanedTerms };
        saveDb(cleanedDb);
        return cleanedDb;
      }
      return parsed;
    } catch {
      // fall through to default
    }
  }
  const initial: DB = {
    terms: seedTerms,
    profiles: seedProfiles,
    user_roles: seedRoles,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
  return initial;
};

const saveDb = (db: DB) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
};

const getSession = (): AuthSession | null => {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
};

const setSession = (session: AuthSession | null) => {
  if (session) {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
};

const listeners = new Set<AuthListener>();
const emitAuth = (event: AuthChangeEvent, session: AuthSession | null) => {
  listeners.forEach(listener => listener(event, session));
};

const likeMatch = (value: unknown, pattern: string) => {
  const text = String(value ?? "").toLowerCase();
  const normalized = pattern.replace(/%/g, "").toLowerCase();
  if (pattern.startsWith("%") && pattern.endsWith("%")) {
    return text.includes(normalized);
  }
  if (pattern.endsWith("%")) {
    return text.startsWith(normalized);
  }
  if (pattern.startsWith("%")) {
    return text.endsWith(normalized);
  }
  return text === normalized;
};

class QueryBuilder {
  private filters: Array<(row: Record<string, unknown>) => boolean> = [];
  private orderBy: { key: string; ascending: boolean } | null = null;
  private limitCount: number | null = null;
  private selectedColumns: string[] | null = null;
  private maybeSingleMode = false;
  private headOnly = false;
  private shouldReturnCount = false;
  private pendingUpdate: Record<string, unknown> | null = null;
  private isDeleteMode = false;
  private pendingInsert: Record<string, unknown> | null = null;

  constructor(private readonly table: keyof DB) {}

  select(columns = "*", options?: { count?: "exact"; head?: boolean }) {
    this.selectedColumns =
      columns === "*" ? null : columns.split(",").map(v => v.trim());
    this.headOnly = !!options?.head;
    this.shouldReturnCount = options?.count === "exact";
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push(row => row[column] === value);
    return this.executeIfMutation();
  }

  ilike(column: string, pattern: string) {
    this.filters.push(row => likeMatch(row[column], pattern));
    return this;
  }

  or(expression: string) {
    const conditions = expression
      .split(",")
      .map(entry => entry.trim())
      .map(entry => {
        const [column, operator, ...rest] = entry.split(".");
        return { column, operator, value: rest.join(".") };
      })
      .filter(entry => entry.operator === "ilike");

    this.filters.push(row =>
      conditions.some(condition =>
        likeMatch(row[condition.column], condition.value)
      )
    );
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.orderBy = { key: column, ascending: options?.ascending !== false };
    return this;
  }

  limit(value: number) {
    this.limitCount = value;
    return this;
  }

  maybeSingle() {
    this.maybeSingleMode = true;
    return this.execute();
  }

  update(patch: Record<string, unknown>) {
    this.pendingUpdate = patch;
    return this;
  }

  delete() {
    this.isDeleteMode = true;
    return this;
  }

  insert(payload: Record<string, unknown> | Record<string, unknown>[]) {
    this.pendingInsert = Array.isArray(payload) ? payload[0] : payload;
    return this.execute();
  }

  then<TResult1 = any, TResult2 = never>(
    onfulfilled?: ((value: any) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ) {
    return this.execute().then(onfulfilled, onrejected);
  }

  private executeIfMutation() {
    if (this.pendingUpdate || this.isDeleteMode) {
      return this.execute();
    }
    return this;
  }

  private async execute() {
    const db = loadDb();
    const rows = [...db[this.table]];
    const matches = rows.filter(row =>
      this.filters.every(filter => filter(row))
    );

    if (this.pendingInsert) {
      const session = getSession();
      const entry = this.createInsertRecord(
        this.pendingInsert,
        session?.user ?? null
      );
      const next = [...(db[this.table] as Record<string, unknown>[]), entry];
      (db[this.table] as Record<string, unknown>[]) = next;
      saveDb(db);
      return { data: entry, error: null };
    }

    if (this.pendingUpdate) {
      const updated = rows.map(row =>
        matches.includes(row)
          ? { ...row, ...this.pendingUpdate, updated_at: nowIso() }
          : row
      );
      (db[this.table] as Record<string, unknown>[]) = updated;
      saveDb(db);
      return { data: null, error: null };
    }

    if (this.isDeleteMode) {
      const updated = rows.filter(row => !matches.includes(row));
      (db[this.table] as Record<string, unknown>[]) = updated;
      saveDb(db);
      return { data: null, error: null };
    }

    let result = matches as Record<string, unknown>[];
    if (this.orderBy) {
      const { key, ascending } = this.orderBy;
      result = result.sort((a, b) => {
        const left = String(a[key] ?? "");
        const right = String(b[key] ?? "");
        return ascending
          ? left.localeCompare(right)
          : right.localeCompare(left);
      });
    }
    if (this.limitCount !== null) {
      result = result.slice(0, this.limitCount);
    }

    if (this.selectedColumns) {
      result = result.map(row => {
        const projected: Record<string, unknown> = {};
        this.selectedColumns?.forEach(column => {
          projected[column] = row[column];
        });
        return projected;
      });
    }

    if (this.headOnly) {
      return {
        data: null,
        count: this.shouldReturnCount ? matches.length : null,
        error: null,
      };
    }

    if (this.maybeSingleMode) {
      return { data: result[0] ?? null, error: null };
    }

    return { data: result, error: null };
  }

  private createInsertRecord(
    payload: Record<string, unknown>,
    user: AuthUser | null
  ) {
    const stamp = nowIso();
    if (this.table === "terms") {
      const normalized: TermRecord = {
        id: String(payload.id ?? genId()),
        word: String(payload.word ?? ""),
        meaning_english: (payload.meaning_english as string | null) ?? null,
        meaning_hindi: (payload.meaning_hindi as string | null) ?? null,
        meaning_gujarati: (payload.meaning_gujarati as string | null) ?? null,
        pronunciation: (payload.pronunciation as string | null) ?? null,
        example_usage: (payload.example_usage as string | null) ?? null,
        status: (payload.status as TermStatus) ?? "pending",
        contributor_email:
          (payload.contributor_email as string | null) ?? user?.email ?? null,
        contributor_name: (payload.contributor_name as string | null) ?? null,
        admin_notes: (payload.admin_notes as string | null) ?? null,
        created_at: (payload.created_at as string) ?? stamp,
        updated_at: (payload.updated_at as string) ?? stamp,
        user_id: (payload.user_id as string | null) ?? user?.id ?? null,
        featured: Boolean(payload.featured),
        audio_url: (payload.audio_url as string | null) ?? null,
      };
      return normalized;
    }

    if (this.table === "profiles") {
      const normalized: ProfileRecord = {
        id: String(payload.id ?? genId()),
        user_id: String(payload.user_id ?? user?.id ?? genId()),
        email: (payload.email as string | null) ?? user?.email ?? null,
        display_name: (payload.display_name as string | null) ?? null,
        is_active: payload.is_active !== false,
        created_at: (payload.created_at as string) ?? stamp,
        updated_at: (payload.updated_at as string) ?? stamp,
      };
      return normalized;
    }

    const normalized: RoleRecord = {
      id: String(payload.id ?? genId()),
      user_id: String(payload.user_id ?? user?.id ?? ""),
      role: (payload.role as UserRole) ?? "user",
      created_at: (payload.created_at as string) ?? stamp,
    };
    return normalized;
  }
}

const uploadToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });

/** Narrow read chains: `QueryBuilder.then` makes TS treat `.eq` as `QueryBuilder | Promise`. */
export type LexiconSelectQuery = {
  eq(column: string, value: unknown): LexiconSelectQuery;
  ilike(column: string, pattern: string): LexiconSelectQuery;
  or(expression: string): LexiconSelectQuery;
  order(
    column: string,
    options?: { ascending?: boolean }
  ): LexiconSelectQuery;
  limit(value: number): LexiconSelectQuery;
  maybeSingle(): Promise<{ data: unknown; error: unknown }>;
};

export function asLexiconSelectChain(q: unknown): LexiconSelectQuery {
  return q as LexiconSelectQuery;
}

export type { AuthChangeEvent, AuthSession as LexiconMockAuthSession };

/** `true` — this file is a browser mock (`localStorage`), not real Supabase or your API user list. */
export const JAIN_LEXICON_MOCK_SUPABASE = true;

export const supabase = {
  from(table: keyof DB) {
    return new QueryBuilder(table);
  },
  auth: {
    async getSession() {
      return { data: { session: getSession() } };
    },
    onAuthStateChange(callback: AuthListener) {
      listeners.add(callback);
      return {
        data: {
          subscription: {
            unsubscribe: () => listeners.delete(callback),
          },
        },
      };
    },
    async signInWithPassword({ email }: { email: string; password: string }) {
      const db = loadDb();
      const profile = db.profiles.find(
        item => item.email?.toLowerCase() === email.toLowerCase()
      );
      if (!profile) {
        return { error: new Error("Invalid credentials") };
      }
      if (!profile.is_active) {
        return { error: new Error("Your account is deactivated") };
      }
      const session: AuthSession = {
        user: { id: profile.user_id, email: profile.email },
      };
      setSession(session);
      emitAuth("SIGNED_IN", session);
      return { error: null };
    },
    async signUp({ email }: { email: string; password: string }) {
      const db = loadDb();
      const exists = db.profiles.some(
        item => item.email?.toLowerCase() === email.toLowerCase()
      );
      if (exists) {
        return { error: new Error("User already exists") };
      }
      const userId = genId();
      const stamp = nowIso();
      db.profiles.push({
        id: genId(),
        user_id: userId,
        email,
        display_name: email.split("@")[0] ?? "User",
        is_active: true,
        created_at: stamp,
        updated_at: stamp,
      });
      db.user_roles.push({
        id: genId(),
        user_id: userId,
        role: "user",
        created_at: stamp,
      });
      saveDb(db);
      return { error: null };
    },
    async signOut() {
      setSession(null);
      emitAuth("SIGNED_OUT", null);
      return { error: null };
    },
  },
  storage: {
    from(_bucket: string) {
      return {
        async upload(path: string, file: File) {
          const publicUrl = await uploadToDataUrl(file);
          localStorage.setItem(`jain-lexicon-audio-${path}`, publicUrl);
          return { data: { path }, error: null };
        },
        getPublicUrl(path: string) {
          const publicUrl =
            localStorage.getItem(`jain-lexicon-audio-${path}`) ?? "";
          return { data: { publicUrl } };
        },
      };
    },
  },
};
